<template>
  <div :style="pageStyle">
    <div class="container py-4 plp-container" style="max-width:560px;">

      <div class="text-center mb-3">
        <img src="@/assets/logo.png" alt="Flockr" style="height:38px;" onerror="this.style.display='none'" />
      </div>

      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-warning"></div>
        <div class="mt-3" :style="mutedStyle">Loading payment details…</div>
      </div>

      <div v-else-if="error" class="card p-4 text-center plp-card" :style="cardStyle">
        <i class="mdi mdi-alert-circle" style="font-size:48px;color:#FF6B6B;"></i>
        <h5 class="mt-3">Couldn't load this payment link</h5>
        <p :style="mutedStyle">{{ error }}</p>
      </div>

      <div v-else>
        <div class="card p-4 mb-3 plp-card" :style="cardStyle">
          <div style="font-size:11px;color:#BFA164;text-transform:uppercase;letter-spacing:1px;font-weight:700;">
            <i class="mdi mdi-cart-outline me-1"></i>Order Payment
          </div>
          <h4 class="mt-2 mb-1">{{ info.kind === 'group' ? 'Pay for cart ' + info.displayRef : 'Pay for order ' + info.displayRef }}</h4>
          <div v-if="info.kind === 'group' && info.orderCount > 1" :style="mutedStyle" style="font-size:13px;">{{ info.orderCount }} orders in this cart</div>

          <div class="my-3 text-center">
            <div :style="mutedStyle" style="font-size:11px;text-transform:uppercase;letter-spacing:1px;">{{ info.state === 'paid' ? 'Total paid' : 'Amount due' }}</div>
            <div class="plp-amount" style="font-size:34px;font-weight:700;color:#BFA164;line-height:1;">{{ info.currency }} {{ formatPrice(info.state === 'paid' ? info.totalDue : outstanding) }}</div>
            <div v-if="info.paidSoFar > 0 && info.state !== 'paid'" :style="mutedStyle" style="font-size:12px;margin-top:6px;">
              Paid so far: <strong style="color:#50C878;">{{ info.currency }} {{ formatPrice(info.paidSoFar) }}</strong> of {{ info.currency }} {{ formatPrice(info.totalDue) }}
            </div>
          </div>

          <div v-if="info.totalDue > 0 && info.paidSoFar > 0 && info.state !== 'paid'" class="mb-3">
            <div style="background:#E8E2DA;border-radius:10px;overflow:hidden;height:10px;">
              <div :style="{width: paidPercent + '%', background:'#2E7D32', height:'100%', transition:'width .3s'}"></div>
            </div>
            <div class="mt-1" :style="mutedStyle" style="font-size:11px;">{{ paidPercent }}% paid</div>
          </div>

          <!-- Fully paid -->
          <div v-if="info.state === 'paid'" style="background:#E6F4E7;color:#2E7D32;padding:12px 14px;border-radius:8px;text-align:center;font-weight:600;">
            <i class="mdi mdi-check-circle me-1"></i>Fully paid — thank you!
          </div>

          <!-- Cancelled -->
          <div v-else-if="info.state === 'cancelled'" style="background:#FDECEA;color:#B3261E;padding:12px 14px;border-radius:8px;text-align:center;font-weight:600;">
            <i class="mdi mdi-cancel me-1"></i>This order was cancelled and can no longer be paid.
          </div>

          <!-- Non-KES -->
          <div v-else-if="info.state === 'non_kes'" style="background:#FFF8E7;border:1px solid #F2D58F;color:#7A5A0A;padding:12px 14px;border-radius:8px;font-size:13px;">
            <i class="mdi mdi-information-outline me-1"></i>This order is priced in {{ info.currency }}. M-Pesa payment is unavailable — please arrange payment with the seller.
          </div>

          <!-- Payable -->
          <div v-else>
            <div v-if="needsSplit && !stkSent" style="background:#FFF8E7;border:1px solid #F2D58F;border-radius:8px;padding:10px 12px;margin-bottom:12px;font-size:12px;color:#7A5A0A;">
              <i class="mdi mdi-information-outline me-1"></i>
              M-Pesa limits single payments to KES {{ formatPrice(stkCeiling) }}. Your KES {{ formatPrice(outstanding) }} will come as several prompts, one at a time.
            </div>

            <div v-if="!stkSent">
              <label :style="mutedStyle" style="font-size:12px;font-weight:600;">M-Pesa phone number</label>
              <div style="position:relative;margin-bottom:12px;">
                <span style="position:absolute;left:12px;top:50%;transform:translateY(-50%);color:#BFA164;font-weight:600;">+254</span>
                <input v-model="phone" type="tel" maxlength="10" placeholder="7XX XXX XXX" class="form-control plp-input" :style="inputStyle" style="padding-left:62px;" />
              </div>
              <button class="btn btn-warning w-100 btn-lg fw-bold plp-btn" :disabled="paying" @click="payNow">
                <span v-if="paying"><span class="spinner-border spinner-border-sm me-2"></span>Sending M-Pesa prompt…</span>
                <span v-else><i class="mdi mdi-cellphone-arrow-down me-1"></i>{{ chunksDone === 0 ? 'Pay' : 'Pay next payment' }} — KES {{ formatPrice(nextChunkAmount) }}<span v-if="splitTotal > 1" style="font-weight:400;opacity:.85;font-size:13px;"> (payment {{ chunksDone + 1 }} of {{ splitTotal }})</span></span>
              </button>
            </div>

            <div v-else style="background:#FFF8E7;border:1px solid #F2D58F;border-radius:8px;padding:14px;text-align:center;">
              <i class="mdi mdi-cellphone-key" style="font-size:32px;color:#BFA164;"></i>
              <div class="mt-2 fw-bold" style="color:#7A5A0A;">Check the phone</div>
              <div style="font-size:13px;color:#7A5A0A;">
                An M-Pesa prompt for <strong>KES {{ formatPrice(nextChunkAmount) }}</strong> was sent to {{ maskedPhone }}.
                <span v-if="splitTotal > 1"> This is payment {{ currentChunkSeq }} of {{ splitTotal }}.</span>
                Enter the PIN to complete.
              </div>
              <div v-if="pollStatus" class="mt-2" style="font-size:12px;color:#7A5A0A;">
                <span class="spinner-border spinner-border-sm me-1" v-if="pollStatus === 'pending'"></span>
                {{ pollMessage }}
              </div>
              <button class="btn btn-sm btn-outline-secondary mt-3" @click="resendStk" :disabled="paying">
                <i class="mdi mdi-refresh me-1"></i>Resend prompt
              </button>
            </div>

            <div v-if="lastChunkSuccess && info.state !== 'paid'" style="background:#E6F4E7;border:1px solid #2E7D32;border-radius:8px;padding:10px 12px;margin-top:12px;font-size:12px;color:#1a5e22;">
              <i class="mdi mdi-check me-1"></i>Payment {{ chunksDone }} received. Tap above to send the next prompt.
            </div>

            <!-- Manual paybill fallback -->
            <div class="mt-4" style="border-top:1px solid rgba(160,152,136,.25);padding-top:14px;">
              <div :style="mutedStyle" style="font-size:12px;font-weight:600;margin-bottom:8px;"><i class="mdi mdi-bank me-1"></i>Or pay manually via M-Pesa Paybill</div>
              <div class="plp-paybill-grid">
                <div :style="pbCellStyle"><span :style="mutedStyle" style="font-size:10px;text-transform:uppercase;">Paybill</span><strong style="color:#BFA164;">{{ info.paybill }}</strong></div>
                <div :style="pbCellStyle"><span :style="mutedStyle" style="font-size:10px;text-transform:uppercase;">Account</span><strong>{{ info.account }}</strong></div>
                <div :style="pbCellStyle"><span :style="mutedStyle" style="font-size:10px;text-transform:uppercase;">Amount</span><strong style="color:#50C878;">KES {{ formatPrice(outstanding) }}</strong></div>
              </div>
              <div :style="mutedStyle" style="font-size:11px;margin-top:8px;"><i class="mdi mdi-information-outline me-1"></i>M-Pesa &gt; Lipa na M-Pesa &gt; Pay Bill &gt; Enter details above</div>
            </div>
          </div>
        </div>

        <p :style="mutedStyle" style="font-size:11px;text-align:center;">
          Payments go to <strong>Flockr Paybill 4160303</strong> and are held safely until the order is delivered.
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.plp-paybill-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px}
@media (max-width: 768px) {
  .plp-container { padding-top: 24px !important; padding-bottom: calc(60px + env(safe-area-inset-bottom, 0px)) !important; padding-left: 12px !important; padding-right: 12px !important; }
  .plp-card { padding: 14px !important; border-radius: 10px !important; }
  .plp-amount { font-size: 26px !important; }
  .plp-input { font-size: 16px !important; }
  .plp-btn { padding: 12px !important; font-size: 14px !important; }
  .plp-paybill-grid { grid-template-columns: 1fr; gap: 6px; }
}
</style>

<script>
/* eslint-disable */
import { executeJson, flocker } from "@/api";

export default {
  name: "PayLinkPage",
  data() {
    return {
      loading: true, error: null,
      info: {},
      phone: '',
      paying: false, stkSent: false, lastChunkSuccess: false,
      stkRequestId: null, currentChunkSeq: 1,
      nextChunkAmount: 0, splitTotal: 1, chunksDone: 0,
      stkCeiling: 150000,
      pollStatus: null, pollMessage: '', pollTimer: null,
      darkMode: (document.documentElement.getAttribute('data-theme') || localStorage.getItem('flockr_theme') || 'dark') !== 'light',
    };
  },
  computed: {
    pageStyle() { return this.darkMode ? 'background:#0A0A0A;min-height:100vh;color:#F5F0E8;' : 'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'; },
    cardStyle() { return this.darkMode ? 'background:#141414;border:1px solid #1E1E1E;border-radius:14px;' : 'background:#FFFFFF;border:1px solid #E8E2DA;border-radius:14px;'; },
    mutedStyle() { return this.darkMode ? 'color:#A09888;' : 'color:#666;'; },
    inputStyle() { return this.darkMode ? 'background:#0F0F0F;border:1px solid #1E1E1E;color:#F5F0E8;' : ''; },
    pbCellStyle() { return (this.darkMode ? 'background:#0F0F0F;border:1px solid #1E1E1E;' : 'background:#F7F5F2;border:1px solid #E8E2DA;') + 'border-radius:6px;padding:10px;display:flex;flex-direction:column;gap:2px;font-size:13px;'; },
    outstanding() { return Math.max(0, Number(this.info.totalDue || 0) - Number(this.info.paidSoFar || 0)); },
    paidPercent() { return this.info.totalDue > 0 ? Math.min(100, Math.round((this.info.paidSoFar / this.info.totalDue) * 100)) : 0; },
    needsSplit() { return this.outstanding > this.stkCeiling; },
    maskedPhone() {
      var p = this.phone || '';
      if (!p) return 'the phone';
      var full = p.length === 9 ? '254' + p : p;
      return full.replace(/(\d{4})\d{3,5}(\d{3})/, '$1***$2');
    },
  },
  mounted() {
    var sp = sessionStorage.getItem('userPhone') || '';
    sp = sp.replace(/[^0-9]/g, '');
    if (sp.indexOf('254') === 0) sp = sp.substring(3);
    if (sp.indexOf('0') === 0) sp = sp.substring(1);
    if (sp.length === 9) this.phone = sp;
    this.fetchInfo();
  },
  beforeUnmount() { if (this.pollTimer) clearInterval(this.pollTimer); },
  methods: {
    formatPrice(v) { return Number(v || 0).toLocaleString(); },
    async fetchInfo() {
      try {
        var r = await executeJson({ function: 'getPayLinkInfo', ref: this.$route.params.ref }, flocker);
        if (r.data && r.data.success && r.data.data) {
          this.info = r.data.data;
          this.nextChunkAmount = Math.min(this.stkCeiling, this.outstanding);
        } else {
          this.error = (r.data && r.data.message) || 'Payment link not found';
        }
      } catch (e) { this.error = 'Network error — please refresh the page'; }
      this.loading = false;
    },
    async payNow() {
      if (!this.phone || this.phone.replace(/[^0-9]/g, '').length < 9) { alert('Enter a valid M-Pesa phone number.'); return; }
      this.paying = true; this.lastChunkSuccess = false;
      try {
        var r = await executeJson({ function: 'payLinkStk', ref: this.$route.params.ref, phone: this.phone }, flocker);
        if (r.data && r.data.success) {
          var d = r.data.data || {};
          this.stkSent = true;
          this.stkRequestId    = d.requestId;
          this.nextChunkAmount = d.amountThisChunk || this.nextChunkAmount;
          this.currentChunkSeq = d.splitSeq || (this.chunksDone + 1);
          this.splitTotal      = d.splitTotal || this.splitTotal;
          if (typeof d.totalDue === 'number')  this.info.totalDue  = d.totalDue;
          if (typeof d.paidSoFar === 'number') this.info.paidSoFar = d.paidSoFar;
          this.startPolling();
        } else {
          alert((r.data && r.data.message) || 'Could not initiate payment');
        }
      } catch (e) { alert('Network error — try again'); }
      this.paying = false;
    },
    async resendStk() {
      this.stkSent = false; this.pollStatus = null;
      if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
      await this.payNow();
    },
    startPolling() {
      var self = this;
      var attempts = 0;
      self.pollStatus = 'pending'; self.pollMessage = 'Waiting for PIN…';
      if (self.pollTimer) clearInterval(self.pollTimer);
      self.pollTimer = setInterval(async function() {
        attempts++;
        if (attempts > 30) { clearInterval(self.pollTimer); self.pollTimer = null; self.pollStatus = 'timeout'; self.pollMessage = 'No response yet — tap Resend if you didn\'t see the prompt.'; return; }
        try {
          var r = await executeJson({ function: 'checkPaymentStatus', requestId: self.stkRequestId }, flocker);
          var d = r.data && r.data.data;
          if (!d) return;
          self.pollStatus = d.status; self.pollMessage = d.message || '';
          if (d.status === 'success') {
            clearInterval(self.pollTimer); self.pollTimer = null;
            await self.fetchInfo();
            self.stkSent = false;
            self.chunksDone = self.chunksDone + 1;
            self.lastChunkSuccess = true;
            self.nextChunkAmount = Math.min(self.stkCeiling, self.outstanding);
          } else if (['cancelled','failed','timeout'].indexOf(d.status) !== -1) {
            clearInterval(self.pollTimer); self.pollTimer = null;
            self.pollStatus = d.status;
          }
        } catch(e) {}
      }, 4000);
    },
  },
};
</script>
